import {CommentModel} from "./commentModel";
import {CommentType, RatingType} from "../../definitions/types";

import {RatingReviewModel} from "./ratingReviewModel";
import {VariantAttributesModel} from "../view/variantAttributesModel";

export class RatingModel {
    private readonly _id: number;
    private readonly _orderNumber: string;
    private readonly _productId: number;
    private readonly _primaryId?: number;
    private readonly _grade: number;
    private readonly _accountId?: number;
    private readonly _name: string;
    private readonly _visible: boolean;
    private readonly _createdOn: Date;
    private readonly _updatedOn?: Date;
    private readonly _review?: RatingReviewModel;
    private readonly _attributes: VariantAttributesModel;
    private readonly _comments: CommentModel[];

    constructor(public readonly ratingType: RatingType) {
        this._id = ratingType.id;
        this._orderNumber = ratingType.orderNumber;
        this._productId = ratingType.productId;
        this._primaryId = ratingType.primaryId;
        this._grade = ratingType.grade;
        this._accountId = ratingType.accountId;
        this._name = ratingType.name;
        this._visible = ratingType.visible;
        this._createdOn = ratingType.created;
        this._updatedOn = ratingType.updated;
        this._review = ratingType.review ? new RatingReviewModel(ratingType.review) : undefined;
        this._attributes = new VariantAttributesModel(ratingType.attributes);
        this._comments = ratingType.comments
            .filter((nullableCommentType): nullableCommentType is CommentType => nullableCommentType !== null)
            .map((commentType) => new CommentModel(commentType));
    }

    /**
     * @method id
     * @description Returns the id of the rating.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @method orderNumber
     * @description Returns the order number of the rating.
     * @returns {string}
     */
    public get orderNumber(): string {
        return this._orderNumber;
    }

    /**
     * @method productId
     * @description Returns the product id of the rating.
     * @returns {number}
     */
    public get productId(): number {
        return this._productId;
    }

    /**
     * @method primaryId
     * @description Returns the primary id of the rating.
     * @returns {number | undefined}
     */
    public get primaryId(): number | undefined {
        return this._primaryId;
    }

    /**
     * @method grade
     * @description Returns the grade of the rating.
     * @returns {number}
     */
    public get grade(): number {
        return this._grade;
    }

    /**
     * @method gradeOutOfFive
     * @description Returns the grade of the rating out of five.
     * @returns {string}
     */
    public get gradeOutOfFive(): string {
        return `(${this.grade}/5)`
    }

    /**
     * @method accountId
     * @description Returns the account id of the rating.
     * @returns {number | undefined}
     */
    public get accountId(): number | undefined {
        return this._accountId;
    }

    /**
     * @method name
     * @description Returns the name of the rating.
     * @returns {string}
     */
    public get name(): string {
        return this._name;
    }

    /**
     * @method visible
     * @description Returns whether the rating is visible.
     * @returns {boolean}
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * @method created
     * @description Returns the creation date of the rating.
     * @returns {Date}
     */
    public get created(): Date {
        return new Date(this._createdOn);
    }

    /**
     * @method createdOn
     * @description Returns the creation date of the rating in a human-readable format.
     * @returns {string}
     */
    public get createdOn(): string {
        return this.created.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    /**
     * @method updated
     * @description Returns the creation date of the rating.
     * @returns {Date | undefined}
     */
    public get updated(): Date | undefined {
        return this._updatedOn ? new Date(this._createdOn) : undefined;
    }

    /**
     * @method updatedOn
     * @description Returns the updated date of the rating in a human-readable format.
     * @returns {string}
     */
    public get updatedOn(): string | undefined {
        return this.updated?.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    /**
     * @method review
     * @description Returns the review of the rating, if it exists.
     * @returns {RatingReviewModel | undefined}
     */
    public get review(): RatingReviewModel | undefined {
        return this._review;
    }

    /**
     * @method comments
     * @description Returns the comments of the rating.
     * @returns {CommentModel[]}
     */
    public get comments(): CommentModel[] {
        return this._comments;
    }

    /**
     * @method hasComments
     * @description Returns the visible comments of the rating.
     * @returns {CommentModel[]}
     */
    public get hasComments(): boolean {
        return this.comments.length > 0;
    }

    /**
     * @method internalComments
     * @description Returns the visible comments of the rating.
     * @returns {CommentModel[]}
     */
    public get internalComments(): CommentModel[] {
        return this.comments.filter((comment) => comment.internal);
    }

    /**
     * @method hasInternalComments
     * @description Returns whether the rating has internal comments.
     * @returns {boolean}
     */
    public get hasInternalComments(): boolean {
        return this.internalComments.length > 0;
    }

    /**
     * @method hasReview
     * @description Returns whether the rating has a review.
     * @returns {boolean}
     */
    public get hasReview(): boolean {
        return !!this.review;
    }

    /**
     * @method hasVisibleReview
     * @description Returns whether the rating has a visible review.
     * @returns {boolean}
     */
    public get hasVisibleReview(): boolean {
        return this.hasReview && this.review!.visible;
    }

    /**
     * @method attributesModel
     * @description Returns the attributes of the rating.
     * @returns {VariantAttributesModel}
     */
    public get attributesModel(): VariantAttributesModel {
        return this._attributes;
    }
}